import { ArtifactMeta } from "../shared/interfaces";

/**
 * Authenticated content endpoint for an artifact's bytes (proxied to ai_engine).
 * `shareId` is the share token a non-owner is viewing through; without it only
 * the conversation's owner is served.
 */
export function artifactContentUrl(artifactId: string, shareId: string | null = null): string {
    const base = `/api/ai/artifacts/${encodeURIComponent(artifactId)}/content`;
    return shareId ? `${base}?share=${encodeURIComponent(shareId)}` : base;
}

/** Interactive HTML the agent rendered — a live view, not a downloadable file. */
function isInteractiveArtifact(artifact: ArtifactMeta): boolean {
    // Backend can send a null mimeType (extensionless / unknown file types), so normalize.
    return (artifact.mimeType ?? "").startsWith("text/html");
}

// Filenames so generic they carry no meaning — fall back to a clean label.
const GENERIC_HTML_NAMES = new Set(["index", "artifact", "app", "main", "page", "interactive"]);

/**
 * User-facing name for an artifact. PDFs/images keep their filename — they ARE files
 * the user can download. An interactive HTML artifact is presented as a titled VIEW,
 * not a file: the extension and "file" vibe are stripped (so the user never sees
 * "fcr_interactive.html"); a humanized, title-cased name is shown instead, falling
 * back to "Interactive view" when the filename is empty or generic.
 */
export function artifactDisplayName(artifact: ArtifactMeta): string {
    if (!isInteractiveArtifact(artifact)) return artifact.filename;

    const base = artifact.filename.replace(/\.[a-z0-9]+$/i, "");
    const words = base.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
    if (!words || GENERIC_HTML_NAMES.has(words.toLowerCase())) return "Interactive view";
    return words.replace(/\b\w/g, (c) => c.toUpperCase());
}
